'use client';

import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface ResponsiveDialogProps {
  children: ReactNode;
  className?: string;
  closeLabel?: string;
  description?: ReactNode;
  dismissible?: boolean;
  footer?: ReactNode;
  onClose: () => void;
  open: boolean;
  title: ReactNode;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (element) =>
      element.getAttribute('aria-hidden') !== 'true' &&
      element.getClientRects().length > 0
  );
}

export function ResponsiveDialog({
  children,
  className,
  closeLabel = 'Cerrar',
  description,
  dismissible = true,
  footer,
  onClose,
  open,
  title,
}: ResponsiveDialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusFrame = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const initialFocus =
        dialog.querySelector<HTMLElement>('[data-dialog-autofocus]') ??
        getFocusableElements(dialog)[0] ??
        dialog;
      initialFocus.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (event.key === 'Escape' && dismissible) {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(dialog);
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      } else if (
        activeElement instanceof Node &&
        !dialog.contains(activeElement)
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [dismissible, onClose, open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="oyb-fade-in fixed inset-0 z-[160] flex items-end justify-center bg-[rgba(4,4,4,0.62)] backdrop-blur-[5px] md:items-center md:p-6"
      onPointerDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'oyb-modal-in flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-[24px] border border-border bg-bg-primary text-text-primary shadow-[0_-24px_80px_rgb(0_0_0/0.42)] outline-none md:max-w-2xl md:rounded-card md:shadow-mockup',
          className
        )}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border bg-bg-secondary/85 px-5 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="font-heading text-xl font-semibold leading-tight text-text-primary sm:text-2xl"
            >
              {title}
            </h2>
            {description ? (
              <div
                id={descriptionId}
                className="mt-2 text-sm leading-6 text-text-secondary"
              >
                {description}
              </div>
            ) : null}
          </div>
          {dismissible ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label={closeLabel}
              className="h-11 w-11 shrink-0 px-0"
            >
              <X size={19} aria-hidden="true" />
            </Button>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 scrollbar-thin sm:px-6">
          {children}
        </div>

        {footer ? (
          <div className="shrink-0 border-t border-border bg-bg-secondary/92 px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pb-5">
            {footer}
          </div>
        ) : (
          <div className="h-[env(safe-area-inset-bottom)] shrink-0 md:hidden" />
        )}
      </div>
    </div>,
    document.body
  );
}
